import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SandboxHeader from '../components/sandbox/SandboxHeader';
import SandboxCanvas from '../components/sandbox/SandboxCanvas';
import ComponentPanel from '../components/sandbox/ComponentPanel';
import LayersPanel from '../components/sandbox/LayersPanel';
import InfoPanel from '../components/sandbox/InfoPanel';
import { useSandboxStore, useAuthStore } from '../stores';
import { sandboxApi, coursesApi } from '../services/api';
import SandboxChoice from '../components/sandbox/SandboxChoice';
import GenerationLoader from '../components/GenerationLoader';

const Sandbox = () => {
    const { token, userInfo } = useAuthStore();
    const { fetchSandboxById } = useSandboxStore();
    const { t } = useTranslation();
    const { sandboxId } = useParams();
    const [searchParams] = useSearchParams();
    const assignmentId = searchParams.get('assignment_id');
    const mode = searchParams.get('mode');
    const processIdParam = searchParams.get('process_id');
    const [componentLoaded, setComponentLoaded] = useState(false);
    const [sandboxData, setSandboxData] = useState(null);
    const [components, setComponents] = useState([]);
    const [relations, setRelations] = useState([]);
    const [activeComponent, setActiveComponent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accessLevel, setAccessLevel] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [processStage, setProcessStage] = useState(0);
    const [updatingComponents, setUpdatingComponents] = useState(false);
    const [isUpdateComplete, setIsUpdateComplete] = useState(false);
    const [currentLayer, setCurrentLayer] = useState(t('sandbox.detailed_architecture'));
    const [assignment, setAssignment] = useState(null);
    const [assignmentStatus, setAssignmentStatus] = useState(null);
    const [loadingAssignment, setLoadingAssignment] = useState(false);
    // Единое состояние для лоадера генерации
    const [generationLoader, setGenerationLoader] = useState({ show: false, processId: null });

    // В компоненте Sandbox добавьте получение aiTutor из sandboxData
    const aiTutor = sandboxData?.ai_tutor_info || null;

    const containerRef = useRef(null);
    const [activeInfoTab, setActiveInfoTab] = useState(null);

    // Загрузка информации о задании, если это режим задания
    useEffect(() => {
        const loadAssignment = async () => {
            if (assignmentId && mode === 'assignment' && token && componentLoaded) {
                setLoadingAssignment(true);
                try {
                    const [assignmentData, statusData] = await Promise.all([
                        coursesApi.getAssignment(assignmentId, token),
                        coursesApi.getAssignmentStatus(assignmentId, token)
                    ]);

                    setAssignment(assignmentData || null);
                    setAssignmentStatus(statusData || null);
                    // Открываем вкладку задания только после загрузки всех данных
                    setActiveInfoTab(t('info_panel.tools.assignment'));
                } catch (error) {
                    console.error('Error loading assignment:', error);
                } finally {
                    setLoadingAssignment(false);
                }
            }
        };

        loadAssignment();
    }, [assignmentId, mode, token, componentLoaded, t]);

    const handleInfoTabChange = useCallback((tabName) => {
        setActiveInfoTab(tabName);
    }, []);

    // Загрузка данных песочницы
    const loadSandboxData = useCallback(async () => {
        try {
            setLoading(true);

            const sandbox = await fetchSandboxById(parseInt(sandboxId), token);

            const instances = await sandboxApi.getInstances(sandboxId, token);
            const instance = instances && instances.length > 0 ? instances[instances.length - 1] : null;
            if (sandbox && instance) {
                sandbox.process_id = instance.process;
            }

            if (!sandbox) {
                setAccessLevel('no-exist');
                setLoading(false);
                return;
            }

            if (sandbox.owner === userInfo.id) {
                setAccessLevel('owner');
            } else if (sandbox.coworkers?.includes(userInfo.id)) {
                setAccessLevel('coworker');
            } else {
                setAccessLevel('no-access');
            }

            setSandboxData(sandbox);

            // Определяем processId и нужно ли показывать лоадер
            let processIdValue = null;
            let shouldShowLoader = false;

            if (processIdParam) {
                const parsed = parseInt(processIdParam);
                if (!isNaN(parsed)) {
                    processIdValue = parsed;
                    shouldShowLoader = true;
                }
            } else if (sandbox.process_id) {
                processIdValue = sandbox.process_id;
                shouldShowLoader = true;
            }

            // Устанавливаем оба значения одновременно
            setGenerationLoader({
                show: shouldShowLoader,
                processId: processIdValue
            });

            if (sandbox.owner === userInfo.id || sandbox.coworkers?.includes(userInfo.id)) {
                let loadedComponents = await sandboxApi.getComponents(sandboxId, token);
                loadedComponents = loadedComponents.filter(
                    (component) => component.category_name !== 'physical_deployment'
                );
                setComponents(loadedComponents);

                if (loadedComponents.length > 0) {
                    const componentIds = loadedComponents.map((comp) => comp.id);
                    const loadedRelations = await sandboxApi.getComponentsRelations(
                        sandboxId,
                        componentIds,
                        token
                    );
                    setRelations(loadedRelations);
                }
            }

            setLoading(false);
            setComponentLoaded(true);
        } catch (error) {
            console.error('Error fetching sandbox data:', error);
            setLoading(false);
        }
    }, [sandboxId, token, userInfo.id, fetchSandboxById, processIdParam]);

    const updateComponentsData = useCallback(async () => {
        setUpdatingComponents(true);
        try {
            let loadedComponents = await sandboxApi.getComponents(sandboxId, token);
            loadedComponents = loadedComponents.filter(
                (component) => component.category_name !== 'physical_deployment'
            );

            // Сохраняем текущие позиции компонентов
            const currentPositions = components.reduce((acc, comp) => {
                acc[comp.id] = {x: comp.x, y: comp.y};
                return acc;
            }, {});

            // Обновляем компоненты, сохраняя их позиции
            const updatedComponents = loadedComponents.map(comp => ({
                ...comp,
                x: currentPositions[comp.id] ? currentPositions[comp.id].x : comp.x,
                y: currentPositions[comp.id] ? currentPositions[comp.id].y : comp.y,
            }));

            setComponents(updatedComponents);

            if (loadedComponents.length > 0) {
                const componentIds = loadedComponents.map((comp) => comp.id);
                const loadedRelations = await sandboxApi.getComponentsRelations(
                    sandboxId,
                    componentIds,
                    token
                );
                setRelations(loadedRelations);
            }
        } catch (error) {
            console.error('Error updating components data:', error);
        } finally {
            setUpdatingComponents(false);
        }
    }, [token, sandboxId, components]);

    // Эффект загрузки данных при изменении sandboxId
    useEffect(() => {
        if (!sandboxId) {
            setShowModal(true);
            setLoading(false);
            return;
        }
        loadSandboxData();
    }, [sandboxId, loadSandboxData]);

    // Эффект для проверки статуса процесса (обновление компонентов)
    useEffect(() => {
        if (!sandboxData || !sandboxData.process_id || isUpdateComplete) return;
        const processId = sandboxData.process_id;

        const interval = setInterval(async () => {
            try {
                const processStage = await sandboxApi.getProcessStage(processId, token);

                if (processStage && processStage.length > 0) {
                    const currentStage = processStage[0].stage;
                    setProcessStage(currentStage);

                    if (currentStage >= 5 && !isUpdateComplete) {
                        await updateComponentsData();
                        setIsUpdateComplete(true);
                        clearInterval(interval);
                    }
                }
            } catch (error) {
                console.error('Error fetching process status:', error);
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [sandboxData, token, updateComponentsData, isUpdateComplete]);


    const filterComponentsByLayer = useCallback((components, layer) => {
        if (layer === t('sandbox.high_level_architecture')) {
            return components.filter(comp => comp.category_name === 'environment');
        } else {
            return components.filter(comp =>
                comp.category_name === 'backend' ||
                comp.category_name === 'frontend' ||
                (comp.category_name === 'environment' && comp.type_name === 'User')
            );
        }
    }, [t]);

    const createHighLevelArchitecture = useCallback((components, sandboxName) => {
        const environmentComponents = components.filter(comp => comp.category_name === 'environment');
        const systemComponent = {
            id: 'system',
            name: sandboxName,
            category_name: 'system',
            x: 0,
            y: 0,
            width: 200,
            height: 100,
        };
        return [...environmentComponents, systemComponent];
    }, []);

    const adjustRelationsForHighLevel = useCallback((relations, components) => {
        return relations.map(relation => {
            const fromComp = components.find(comp => comp.id === relation.from_component);
            const toComp = components.find(comp => comp.id === relation.to_component);
            if (fromComp && toComp) {
                if (fromComp.category_name === 'environment' &&
                    (toComp.category_name === 'backend' || toComp.category_name === 'frontend')) {
                    return {...relation, to_component: 'system'};
                }
                if (toComp.category_name === 'environment' &&
                    (fromComp.category_name === 'backend' || fromComp.category_name === 'frontend')) {
                    return {...relation, from_component: 'system'};
                }
            }
            return relation;
        }).filter(relation =>
            components.some(comp => comp.id === relation.from_component) &&
            components.some(comp => comp.id === relation.to_component)
        );
    }, []);

    const handleLayerChange = useCallback((layer) => {
        setCurrentLayer(layer);
    }, []);

    const handleRegenerate = useCallback((newProcessId) => {
        setGenerationLoader({
            show: true,
            processId: newProcessId
        });
    }, []);

    const filteredComponents = useMemo(() => {
        if (currentLayer === t('sandbox.high_level_architecture')) {
            return createHighLevelArchitecture(components, sandboxData?.name);
        } else {
            return filterComponentsByLayer(components, currentLayer);
        }
    }, [components, currentLayer, sandboxData, createHighLevelArchitecture, filterComponentsByLayer, t]);

    const filteredRelations = useMemo(() => {
        if (currentLayer === t('sandbox.high_level_architecture')) {
            return adjustRelationsForHighLevel(relations, components);
        } else {
            return relations.filter(relation =>
                filteredComponents.some(comp => comp.id === relation.from_component) &&
                filteredComponents.some(comp => comp.id === relation.to_component)
            );
        }
    }, [relations, currentLayer, components, filteredComponents, adjustRelationsForHighLevel, t]);

    // Обработчик завершения генерации (скрывает лоадер)
    const handleGenerationComplete = useCallback(async () => {
        await loadSandboxData();
        setGenerationLoader(prev => ({ ...prev, show: false }));
    }, [loadSandboxData]);

    if (loading || loadingAssignment) {
        return <div>{t('sandbox.loading')}</div>;
    }

    if (accessLevel === 'no-exist') {
        return <div>{t('sandbox.not_exists')}</div>;
    }

    if (accessLevel === 'no-access') {
        return <div>{t('sandbox.no_access')}</div>;
    }

    return (
        <div className="w-full flex flex-col" style={{height: 'calc(100vh - 90px)', position: 'relative'}}>
            {showModal && (
                <SandboxChoice token={token} userInfo={userInfo} onClose={() => setShowModal(false)} />
            )}

            {generationLoader.show && generationLoader.processId && (
                <GenerationLoader
                    token={token}
                    processId={generationLoader.processId}
                    sandboxId={sandboxId}
                    onComplete={handleGenerationComplete}
                />
            )}

            <div className="flex-shrink-0">
                <SandboxHeader
                    token={token}
                    userInfo={userInfo}
                    sandboxName={sandboxData ? sandboxData.name : ''}
                    showRecreateButton={accessLevel === 'owner' && mode !== 'assignment'}
                    sandboxId={sandboxId}
                    stageRef={containerRef}
                    onRegenerate={handleRegenerate}
                />
            </div>

            <div className="flex-grow flex relative">
                <div className="flex-grow relative" ref={containerRef}>
                    <SandboxCanvas
                        components={filteredComponents}
                        setComponents={setComponents}
                        setActiveComponent={setActiveComponent}
                        relations={filteredRelations}
                        containerRef={containerRef}
                        updatingComponents={updatingComponents}
                        currentLayer={currentLayer}
                        sandboxName={sandboxData ? sandboxData.name : ''}
                        onLayerChange={handleLayerChange}
                        userInfo={userInfo}
                        token={token}
                        sandboxId={sandboxId}
                        activeComponent={activeComponent}
                        activeInfoTab={activeInfoTab}
                        processStage={processStage}
                        isAssignmentMode={mode === 'assignment'}
                        aiTutor={aiTutor}
                    />
                </div>

                <div className="flex-shrink-0">
                    <InfoPanel
                        activeComponent={activeComponent}
                        onTabChange={handleInfoTabChange}
                        token={token}
                        assignment={assignment}
                        assignmentStatus={assignmentStatus}
                        activeTab={activeInfoTab}
                    />
                </div>

                <LayersPanel
                    currentLayer={currentLayer}
                    onLayerChange={handleLayerChange}
                />

                <ComponentPanel
                    components={filteredComponents}
                    setComponents={setComponents}
                    setActiveComponent={setActiveComponent}
                />
            </div>
        </div>
    );
};

export default Sandbox;